import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@/utils/format';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { EpcEstimateBoqWorkbench } from './EpcEstimateBoqWorkbench';
import type { FormInstance } from 'antd';
import { EpcEstimatingWorkbenchSurface } from './workbench/EpcEstimatingWorkbenchSurface';
import type { HealthIssue } from './workbench/EpcEstimatingWorkbenchSurface';
import { EpcEstimateBoqImportWizard } from './EpcEstimateBoqImportWizard';
import { EpcEstimateBoqAddDrawer } from './EpcEstimateBoqAddDrawer';

const { Text } = Typography;

export type Est3Density = 'compact' | 'standard' | 'detail';
export type BoqCoverageChipFilter = 'all' | 'without_cost' | 'unlinked';

export interface EpcEstimateBoqLineWorkspaceProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  boqLines: any[];
  costLines: any[];
  boqLoading?: boolean;
  boqForm: FormInstance;
  onAddBoq: (values: Record<string, unknown>) => void;
  boqAddPending?: boolean;
  onBoqImported: () => void;
  onClearBoq?: () => void;
  boqRollups?: Map<
    string,
    {
      line_count: number;
      total_amount: number;
      sell_amount?: number;
      margin_pct?: number | null;
      coverage_status?: string;
    }
  >;
  selectedBoqLineId: string | null;
  onSelectBoqLine: (lineId: string | null) => void;
  onAddCostForBoq?: (boqLineId: string) => void;
  onOpenCostDetail?: (line: Record<string, unknown>) => void;
  onOpenMaterials?: (lineId: string) => void;
  density?: Est3Density;
  /** UX3.2 — full-width table, row opens drawer */
  immersive?: boolean;
  onOpenBoqLine?: (lineId: string) => void;
  coverageChipFilter?: BoqCoverageChipFilter;
  onCoverageChipFilterChange?: (f: BoqCoverageChipFilter) => void;
  importOpen?: boolean;
  onImportOpenChange?: (open: boolean) => void;
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
  /** NextGen P1 — Estimating Workbench grid surface */
  useEstimatingWorkbench?: boolean;
  wbsNodes?: any[];
  healthIssues?: HealthIssue[];
  onBatchSaveBoq?: React.ComponentProps<typeof EpcEstimatingWorkbenchSurface>['onBatchSave'];
  batchSavePending?: boolean;
  jumpToBoqId?: string | null;
  onOpenBuildUp?: (line: Record<string, unknown>) => void;
  /** After Workbench COSTING mutations — parent reloads BOQ / cost. */
  onWorkbenchMutated?: () => void;
}

/**
 * EST3 UX3 — BOQ-centric master workspace (tree + detail + linked costs).
 */
export const EpcEstimateBoqLineWorkspace: React.FC<EpcEstimateBoqLineWorkspaceProps> = ({
  estimateId,
  revisionId,
  readOnly,
  boqLines,
  costLines,
  boqLoading,
  boqForm,
  onAddBoq,
  boqAddPending,
  onBoqImported,
  onClearBoq,
  boqRollups,
  selectedBoqLineId,
  onSelectBoqLine,
  onAddCostForBoq,
  onOpenCostDetail,
  onOpenMaterials,
  immersive,
  onOpenBoqLine,
  coverageChipFilter,
  onCoverageChipFilterChange,
  importOpen,
  onImportOpenChange,
  addOpen,
  onAddOpenChange,
  useEstimatingWorkbench,
  wbsNodes,
  healthIssues,
  onBatchSaveBoq,
  batchSavePending,
  jumpToBoqId,
  onOpenBuildUp,
  onWorkbenchMutated,
}) => {
  const detailQ = useQuery({
    queryKey: ['epc-est-boq-detail', estimateId, revisionId, selectedBoqLineId],
    queryFn: () =>
      epcEstimationService.getClientBoqLineDetail(estimateId, revisionId, selectedBoqLineId!),
    enabled: !!estimateId && !!revisionId && !!selectedBoqLineId && !immersive,
  });

  const linkedCosts = immersive
    ? []
    : detailQ.data?.linked_cost_lines
      ? detailQ.data.linked_cost_lines
      : selectedBoqLineId
        ? costLines.filter((c) => c.client_boq_line_id === selectedBoqLineId)
        : [];

  const boqLine = detailQ.data?.boq_line;
  const costTotal = detailQ.data?.cost_total ?? linkedCosts.reduce(
    (a, c) => a + Number(c.total_amount || 0),
    0
  );
  const sellAmount = Number(boqLine?.sell_amount ?? detailQ.data?.sell_amount ?? 0);
  const marginPct = detailQ.data?.margin_pct;

  const workbench = (
    <EpcEstimateBoqWorkbench
      estimateId={estimateId}
      revisionId={revisionId}
      readOnly={readOnly}
      lines={boqLines}
      loading={boqLoading}
      boqForm={boqForm}
      onAdd={onAddBoq}
      addPending={boqAddPending}
      onImported={onBoqImported}
      onClearBoq={onClearBoq}
      boqRollups={boqRollups}
      selectedBoqLineId={selectedBoqLineId}
      onBoqLineSelect={immersive ? undefined : onSelectBoqLine}
      onShowLinkedCosts={immersive ? undefined : (id) => onSelectBoqLine(id)}
      immersive={immersive}
      coverageChipFilter={coverageChipFilter}
      onCoverageChipFilterChange={onCoverageChipFilterChange}
      onRowOpen={immersive ? onOpenBoqLine : undefined}
      importOpen={importOpen}
      onImportOpenChange={onImportOpenChange}
      addOpen={addOpen}
      onAddOpenChange={onAddOpenChange}
    />
  );

  if (immersive && useEstimatingWorkbench && onBatchSaveBoq) {
    return (
      <>
        <EpcEstimatingWorkbenchSurface
          estimateId={estimateId}
          revisionId={revisionId}
          boqLines={boqLines}
          costLines={costLines}
          wbsNodes={wbsNodes}
          boqLoading={boqLoading}
          readOnly={readOnly}
          selectedBoqLineId={selectedBoqLineId}
          onSelectBoqLine={onSelectBoqLine}
          boqRollups={boqRollups}
          coverageFilter={coverageChipFilter}
          onCoverageFilterChange={onCoverageChipFilterChange}
          onBatchSave={onBatchSaveBoq}
          savePending={batchSavePending}
          onAddCost={onAddCostForBoq}
          onOpenCostDetail={onOpenCostDetail}
          onOpenBuildUp={onOpenBuildUp}
          healthIssues={healthIssues}
          jumpToId={jumpToBoqId}
          onMutated={onWorkbenchMutated}
        />
        {/* Legacy workbench is skipped in NextGen surface — keep import/add overlays mounted. */}
        <EpcEstimateBoqImportWizard
          open={!!importOpen}
          onClose={() => onImportOpenChange?.(false)}
          estimateId={estimateId}
          revisionId={revisionId}
          readOnly={readOnly}
          onImported={onBoqImported}
        />
        <EpcEstimateBoqAddDrawer
          open={!!addOpen}
          onClose={() => onAddOpenChange?.(false)}
          form={boqForm}
          readOnly={readOnly}
          lines={boqLines || []}
          onSubmit={onAddBoq}
          pending={boqAddPending}
          defaultParentId={selectedBoqLineId}
        />
      </>
    );
  }

  if (immersive) {
    return workbench;
  }

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={10} xl={9}>
        {workbench}
      </Col>
      <Col xs={24} lg={14} xl={15}>
        {!selectedBoqLineId ? (
          <Card size="small">
            <Alert
              type="info"
              showIcon
              message="Select a BOQ line"
              description="Choose a client BOQ line to view schedule, linked costs, sell, and margin."
            />
          </Card>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small" title={boqLine?.description || 'BOQ line'} loading={detailQ.isLoading}>
              <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
                <Descriptions.Item label="Item">{boqLine?.item_number || '—'}</Descriptions.Item>
                <Descriptions.Item label="Qty">{boqLine?.quantity ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Schedule">
                  {formatMoney(boqLine?.amount, { display: 'plain', maximumFractionDigits: 0 })}
                </Descriptions.Item>
                <Descriptions.Item label="Cost total">
                  {formatMoney(costTotal, { display: 'plain', maximumFractionDigits: 0 })}
                </Descriptions.Item>
                <Descriptions.Item label="Sell">
                  {formatMoney(sellAmount, { display: 'plain', maximumFractionDigits: 0 })}
                </Descriptions.Item>
                <Descriptions.Item label="Margin">
                  {marginPct != null ? `${marginPct}%` : '—'}
                </Descriptions.Item>
              </Descriptions>
              {!readOnly && onAddCostForBoq ? (
                <Button
                  type="primary"
                  size="small"
                  style={{ marginTop: 12 }}
                  onClick={() => onAddCostForBoq(selectedBoqLineId)}
                >
                  Add cost line
                </Button>
              ) : null}
            </Card>

            <Card
              size="small"
              title={
                <Space>
                  <span>Linked costs</span>
                  <Tag>{linkedCosts.length}</Tag>
                </Space>
              }
            >
              <Table
                size="small"
                rowKey="id"
                dataSource={linkedCosts}
                pagination={linkedCosts.length > 25 ? { pageSize: 25 } : false}
                onRow={(row) => ({
                  onClick: () => onOpenCostDetail?.(row),
                  style: { cursor: onOpenCostDetail ? 'pointer' : undefined },
                })}
                columns={[
                  { title: 'Description', dataIndex: 'description', ellipsis: true },
                  { title: 'Kind', dataIndex: 'resource_kind', width: 90 },
                  {
                    title: 'Total',
                    dataIndex: 'total_amount',
                    width: 100,
                    render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                  },
                  {
                    title: '',
                    width: 80,
                    render: (_: unknown, row: any) =>
                      row.resource_kind === 'MATERIAL' && onOpenMaterials ? (
                        <Button
                          size="small"
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenMaterials(row.id);
                          }}
                        >
                          Build-up
                        </Button>
                      ) : null,
                  },
                ]}
              />
            </Card>
          </Space>
        )}
      </Col>
    </Row>
  );
};
